import { type ShortUrl } from '@air/api';
import { type Board, type Clip, ShortUrlObjectType } from '@air/api/types';

import { type AssetShareLinkModalClip } from '~/components/SharePrivateClipModal/types';
import { type WorkspaceAgentShareLinkMonikerPreviewMedia } from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentShareLinkMonikerPreviewMedia';
import { getClipDisplayTitle } from '~/utils/getClipDisplayTitle';

/** What the card body opens inside the product; form and canvas links have no in-product target. */
export type WorkspaceAgentShareLinkMonikerOpenTarget =
  | { clipId: string; kind: 'asset' }
  | { board: Pick<Board, 'id' | 'title'>; kind: 'board' };

/**
 * The share-settings modal that target already uses from its own view. Asset settings carry the
 * linked clip so the modal opens on that version, the same way the asset modal does.
 */
export type WorkspaceAgentShareLinkMonikerSettingsTarget =
  | { clip: AssetShareLinkModalClip; kind: 'asset' }
  | { boardId: string; kind: 'board' }
  | { canvasId: string; kind: 'canvas' }
  | { boardId: string; boardTitle: string; kind: 'form' };

export type WorkspaceAgentShareLinkMonikerSubject = {
  openTarget: WorkspaceAgentShareLinkMonikerOpenTarget | null;
  previewMedia: WorkspaceAgentShareLinkMonikerPreviewMedia | null;
  settingsTarget: WorkspaceAgentShareLinkMonikerSettingsTarget | null;
  title: string;
};

type ShortUrlGetByShortIdResponse = Awaited<ReturnType<typeof ShortUrl.getByShortId>>;

type ShareLinkClipSource = Pick<Clip, 'assets' | 'ext' | 'importedName' | 'title' | 'type'>;

const DEFAULT_SHARE_LINK_TITLE = 'Share link';

/** The preview card shows the public URL without a scheme or a leading `app.` host label. */
export const toWorkspaceAgentShareLinkDisplayUrl = (shareUrl: string) =>
  shareUrl.replace(/^https?:\/\//, '').replace(/^app\./, '');

const toShareLinkClipFields = (
  shortUrlClip: ShareLinkClipSource,
  workspaceClip?: ShareLinkClipSource,
): ShareLinkClipSource => (workspaceClip ? { ...shortUrlClip, ...workspaceClip } : shortUrlClip);

const toShareLinkVersionLabel = (isDefault: boolean, version: number) =>
  isDefault ? `Current · V${version}` : `V${version}`;

type ShareLinkAssetSettingsClip = Pick<Clip, 'assetId' | 'assetVersionCount' | 'ext' | 'id' | 'version'> & {
  isDefault: boolean;
};

const toShareLinkAssetSettingsTarget = (
  clip: ShareLinkAssetSettingsClip,
): WorkspaceAgentShareLinkMonikerSettingsTarget => ({
  clip: {
    assetId: clip.assetId,
    assetVersionCount: clip.assetVersionCount,
    ext: clip.ext,
    id: clip.id,
    isDefault: clip.isDefault,
    version: clip.version,
  },
  kind: 'asset',
});

const toTextOnlyShareLinkMonikerSubject = (
  title: string | null | undefined,
  settingsTarget: WorkspaceAgentShareLinkMonikerSettingsTarget | null,
): WorkspaceAgentShareLinkMonikerSubject => ({
  openTarget: null,
  previewMedia: null,
  settingsTarget,
  title: title || DEFAULT_SHARE_LINK_TITLE,
});

export const toWorkspaceAgentShareLinkMonikerSubject = (
  response: ShortUrlGetByShortIdResponse,
  workspaceClip?: ShareLinkClipSource,
): WorkspaceAgentShareLinkMonikerSubject => {
  switch (response.type) {
    case ShortUrlObjectType.board: {
      const board = response.data.board;
      const coverImageUrl = board.coverImage?.url || board.thumbnails?.[0] || null;
      const title = board.title || DEFAULT_SHARE_LINK_TITLE;

      return {
        openTarget: { board: { id: board.id, title }, kind: 'board' },
        previewMedia: { kind: 'board', thumbnailUrl: coverImageUrl },
        settingsTarget: { boardId: board.id, kind: 'board' },
        title,
      };
    }
    case ShortUrlObjectType.clip:
    case ShortUrlObjectType.asset: {
      const clip = toShareLinkClipFields(response.data, workspaceClip);
      const { isDefault, version } = response.data;

      return {
        openTarget: { clipId: response.data.id, kind: 'asset' },
        previewMedia: {
          clipType: clip.type,
          ext: clip.ext,
          kind: 'asset',
          thumbnailUrl: clip.assets.image || null,
          versionLabel: toShareLinkVersionLabel(isDefault, version),
        },
        settingsTarget: toShareLinkAssetSettingsTarget(response.data),
        title: getClipDisplayTitle(clip, DEFAULT_SHARE_LINK_TITLE),
      };
    }
    case ShortUrlObjectType.form:
      return toTextOnlyShareLinkMonikerSubject(response.data.title, {
        boardId: response.data.boardId,
        boardTitle: response.data.title || DEFAULT_SHARE_LINK_TITLE,
        kind: 'form',
      });
    case ShortUrlObjectType.canvas:
      return toTextOnlyShareLinkMonikerSubject(response.data.title, {
        canvasId: response.data.id,
        kind: 'canvas',
      });
    default:
      return toTextOnlyShareLinkMonikerSubject(undefined, null);
  }
};
